/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { appHistoryPush } from '@/main/modules/app/appHistory';
import { makeSelectGuardComponentEnabled } from '@/main/modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '@/main/modules/wizard/wizardActions';
import { EWizardName } from '@/main/modules/wizard/wizardConstants';
import { icons } from '@assets/index';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { ExternalRoutePath, openExternalRoute } from '@routes/constants/externalRoutes';
import { CLIENT_ID, IRouteParams, RoutePath } from '@routes/constants/routesConstants';
import { stringFormat } from '@utils/stringFormat';

import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';

const useTableFooterButtonReconciliation = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
) => {
    const dispatch = useDispatch();

    const { clientId } = useParams<IRouteParams>();

    const isWizardCollationReportEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardCollationReport),
    );

    return {
        className: 'table-footer__link link-reconciliation',
        dataName: 'reconciliation',
        icon: icons.compareIcon,
        id: AccessComponentKeyEnum.FooterButtonReconciliation,
        onboarding: EOnboardingComponentsId.accountsTableFooterButtonReconciliation,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonReconciliation),

        onClick: useCallback(() => {
            dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.COLLATION_REPORT }));
            logBusinessEvent(ELogBusinessEventName.USER_R_COLLATION_MASTER_TO_STEP_1, ELogBusinessEventType.common);
            const urlConfig = mapTableFooterNameToUrl[tableFooterName];
            if (!urlConfig) return;

            isWizardCollationReportEnabled
                ? appHistoryPush(urlConfig.url.replace(CLIENT_ID, clientId))
                : openExternalRoute(stringFormat(urlConfig.external, clientId));
        }, [dispatch, logBusinessEvent, tableFooterName, isWizardCollationReportEnabled, clientId]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.account]: {
        url: RoutePath.wizardCollationReportFromAccounts,
        external: ExternalRoutePath.accountCollation,
    },
    [TableFooterNameType.accountId]: {
        url: RoutePath.wizardCollationReportFromAccountId,
        external: ExternalRoutePath.accountCollationId,
    },
    [TableFooterNameType.bills]: {
        url: RoutePath.wizardCollationReportFromAccounts,
        external: ExternalRoutePath.accountCollation,
    },
    [TableFooterNameType.billsUnpaid]: {
        url: RoutePath.wizardCollationReportFromAccounts,
        external: ExternalRoutePath.accountCollation,
    },
    [TableFooterNameType.billsAccountId]: {
        url: RoutePath.wizardCollationReportFromAccountId,
        external: ExternalRoutePath.accountCollationId,
    },
    [TableFooterNameType.billsUnpaidAccountId]: {
        url: RoutePath.wizardCollationReportFromAccountId,
        external: ExternalRoutePath.accountCollationId,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.bills]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonReconciliation,
    },
    [TableFooterNameType.billsUnpaid]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonReconciliation,
    },
    [TableFooterNameType.billsAccountId]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonReconciliation,
    },
    [TableFooterNameType.billsUnpaidAccountId]: {
        onboarding: EOnboardingComponentsId.billsTableFooterButtonReconciliation,
    },
};

export default useTableFooterButtonReconciliation;
